import { FunctionComponent, useMemo } from "react";
import { CarIcon } from "@icons/CarIcon";
import { Leg } from "@dto/Direction";
import { formatDistance, formatDistanceForLegs } from "@lib/calculateDistance";
import { formatDuration, formatDurationForLegs } from "@lib/calculateDuration";

import "./PlanItemTravelIndicator.css";
import { isNil } from "@lib/isNil";
import { useAppSelector } from "@store/store";
import { selectTravelMode } from "@store/planRouteReducer";
import { PersonIcon } from "@icons/PersonIcon";

interface PlanItemTravelIndicatorProps {
  leg?: Leg | undefined;
  distanceInMeters?: number;
  timeInSeconds?: number;
}

const PlanItemTravelIndicator: FunctionComponent<
  PlanItemTravelIndicatorProps
> = ({ leg, distanceInMeters, timeInSeconds }) => {
  const travelMode = useAppSelector(selectTravelMode);
  const { durationFormatted, unit: durationUnit } = useMemo(() => {
    if (!isNil(timeInSeconds)) {
      return formatDuration(timeInSeconds!);
    }

    return formatDurationForLegs(leg ? [leg] : []);
  }, [leg, timeInSeconds]);

  const { distanceFormatted, unit: distanceUnit } = useMemo(() => {
    if (!isNil(distanceInMeters)) {
      return formatDistance(distanceInMeters!);
    }

    return formatDistanceForLegs(leg ? [leg] : []);
  }, [distanceInMeters, leg]);

  if (isNil(distanceInMeters) && isNil(timeInSeconds) && isNil(leg)) {
    return <></>;
  }

  if (distanceInMeters === 0) {
    return <></>;
  }

  return (
    <div className="plan-item-travel-indicator-component">
      <div className="content">
        <span className="icon">
          {travelMode === "WALK" ? (
            <PersonIcon size={16} />
          ) : (
            <CarIcon size={16} />
          )}
        </span>
        <span className="duration">
          {durationFormatted} {durationUnit}
        </span>
        <span className="distance">
          {distanceFormatted} {distanceUnit}
        </span>
      </div>
    </div>
  );
};

export { PlanItemTravelIndicator };
